import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Users, UserCheck, GraduationCap, Target, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { OrgStatTile } from '@/features/OrgLayout/primitives/OrgStatTile';
import { useOrgStudents } from '@/lib/hooks/useOrg';
import { BAND_META, BAND_ORDER } from '@/features/OrgLayout/readiness';
import type { ReadinessBand } from '@/lib/api/org';

type SkillBand = 'strong' | 'ok' | 'weak' | 'critical';
const SKILL_BANDS: SkillBand[] = ['strong', 'ok', 'weak', 'critical'];
const SKILL_BAND_LABEL: Record<SkillBand, string> = { strong: 'Strong', ok: 'OK', weak: 'Weak', critical: 'Critical' };
const SKILL_BAND_CHIP: Record<SkillBand, string> = {
  strong: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  ok: 'border-lime-200 bg-lime-50 text-lime-700',
  weak: 'border-amber-200 bg-amber-50 text-amber-700',
  critical: 'border-red-200 bg-red-50 text-red-700',
};
function skillBandOf(v: number): SkillBand {
  return v >= 75 ? 'strong' : v >= 50 ? 'ok' : v >= 30 ? 'weak' : 'critical';
}

export const Route = createFileRoute('/org/students')({
  validateSearch: (s: Record<string, unknown>): { skill?: string; skillBand?: SkillBand } => ({
    skill: typeof s.skill === 'string' && s.skill ? s.skill : undefined,
    skillBand: SKILL_BANDS.includes(s.skillBand as SkillBand) ? (s.skillBand as SkillBand) : undefined,
  }),
  component: OrgStudentsPage,
});

const PAGE_SIZE = 10;
const SELECT_TRIGGER = 'h-[42px] w-auto min-w-[150px] rounded-[14px] border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280]';
const BAND_SCORE: Record<ReadinessBand, string> = {
  product_ready: 'text-emerald-600', service_ready: 'text-blue-600', developing: 'text-amber-600', needs_support: 'text-red-600',
};

function Header() {
  return (
    <div>
      <h1 className="text-[36px] font-bold leading-tight text-[#111827]">Students</h1>
      <p className="pt-1 text-base text-[#6b7280]">All students across your organization&apos;s batches.</p>
    </div>
  );
}

function OrgStudentsPage() {
  const { data, isLoading, isError, refetch } = useOrgStudents();
  const navigate = useNavigate();
  const { skill, skillBand } = Route.useSearch();
  const [search, setSearch] = useState('');
  const [batch, setBatch] = useState('all');
  const [band, setBand] = useState('all');
  const [page, setPage] = useState(1);

  const batches = useMemo(() => {
    const m = new Map<string, string>();
    (data ?? []).forEach((s) => m.set(s.batch_id, s.batch_name));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((s) => {
      if (batch !== 'all' && s.batch_id !== batch) return false;
      if (band !== 'all' && s.readiness_band !== band) return false;
      if (skill && skillBand) {
        const sc = s.skill_scores?.[skill];
        if (sc === undefined || skillBandOf(sc) !== skillBand) return false;
      }
      if (q && !s.name.toLowerCase().includes(q) && !s.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, batch, band, skill, skillBand]);

  const readyCount = (data ?? []).filter((s) => s.readiness_band === 'product_ready' || s.readiness_band === 'service_ready').length;
  const avgReadiness = (data && data.length) ? Math.round(data.reduce((a, s) => a + s.readiness_score, 0) / data.length) : 0;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const resetPage = () => setPage(1);

  const goToStudent = (batchId: string, userId: string) =>
    navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId, userId } });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Header />
        <Skeleton className="h-[120px] rounded-[24px]" />
        <Skeleton className="h-96 rounded-[24px]" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="space-y-6">
        <Header />
        <div className="flex flex-col items-center py-16 text-center">
          <p className="text-[#6b7280]">Couldn&apos;t load students.</p>
          <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }
  if ((data?.length ?? 0) === 0) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState icon={Users} title="No students yet" description="Once students are enrolled across your batches, they'll appear here." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header />

      {/* Summary tiles */}
      <OrgCard className="flex overflow-hidden">
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={Users} iconBg="#fff4f0" iconColor="#ff5a1f" value={data!.length} label="Total Students" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={UserCheck} iconBg="#f0fdf4" iconColor="#16a34a" value={readyCount} label="Placement Ready" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={GraduationCap} iconBg="#eff6ff" iconColor="#2563eb" value={batches.length} label="Batches" /></div>
        <div className="flex-1"><OrgStatTile icon={Target} iconBg="#f0fdfa" iconColor="#0f766e" value={avgReadiness} label="Avg Readiness" /></div>
      </OrgCard>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-[42px] w-[300px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
          <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }} placeholder="Search by name or email..." className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none" />
        </div>
        <Select value={batch} onValueChange={(v) => { setBatch(v); resetPage(); }}>
          <SelectTrigger className={SELECT_TRIGGER}><SelectValue placeholder="All Batches" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Batches</SelectItem>
            {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={band} onValueChange={(v) => { setBand(v); resetPage(); }}>
          <SelectTrigger className={SELECT_TRIGGER}><SelectValue placeholder="All Readiness" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Readiness</SelectItem>
            {BAND_ORDER.map((b) => <SelectItem key={b} value={b}>{BAND_META[b].label}</SelectItem>)}
          </SelectContent>
        </Select>
        {skill && skillBand && (
          <button
            type="button"
            onClick={() => { navigate({ to: '/org/students', search: {} }); resetPage(); }}
            className={cn('inline-flex h-[42px] items-center gap-2 rounded-[14px] border px-[15px] text-sm font-semibold', SKILL_BAND_CHIP[skillBand])}
            title="Clear skill filter"
          >
            {skill}: {SKILL_BAND_LABEL[skillBand]}
            <span className="text-base leading-none opacity-70">×</span>
          </button>
        )}
      </div>

      {/* Table */}
      <OrgCard className="overflow-hidden">
        {pageItems.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9ca3af]">No students match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-[#ececec]">
                  {['Student', 'Batch', 'Readiness', 'Score', 'Completion', ''].map((h, i) => (
                    <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s, i) => (
                  <tr key={`${s.batch_id}-${s.user_id}`} onClick={() => goToStudent(s.batch_id, s.user_id)} className={cn('cursor-pointer hover:bg-[#fafafa]', i !== pageItems.length - 1 && 'border-b border-[#ececec]')}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <OrgAvatar name={s.name} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                          <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#374151]">{s.batch_name}</td>
                    <td className="px-6 py-4">
                      <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', BAND_META[s.readiness_band].badgeClass)}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', BAND_META[s.readiness_band].dotClass)} />
                        {BAND_META[s.readiness_band].label}
                      </span>
                    </td>
                    <td className={cn('px-6 py-4 text-sm font-bold tabular-nums', BAND_SCORE[s.readiness_band])}>{Math.round(s.readiness_score)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <OrgProgressBar pct={s.completion_pct} className="w-[80px]" barClassName="bg-[#0f766e]" />
                        <span className="text-sm tabular-nums text-[#111827]">{Math.round(s.completion_pct)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right"><ChevronRight className="inline h-4 w-4 text-[#9ca3af]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[#ececec] px-6 py-4">
          <p className="text-sm text-[#9ca3af]">{filtered.length === 0 ? 'Showing 0 students' : `Showing ${from} to ${to} of ${filtered.length} students`}</p>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous page" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40"><ChevronLeft className="h-[14px] w-[14px]" /></button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              return <button key={n} type="button" onClick={() => setPage(n)} className={cn('flex h-[34px] w-[34px] items-center justify-center rounded-[14px] text-sm font-semibold', n === currentPage ? 'bg-[#111827] text-white' : 'border border-[#ececec] text-[#111827]')}>{n}</button>;
            })}
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40"><ChevronRight className="h-[14px] w-[14px]" /></button>
          </div>
        </div>
      </OrgCard>
    </div>
  );
}
